import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Microphone, PaperPlaneTilt, Stop } from "@phosphor-icons/react";

export default function MessagesPanel({ presetClientId }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    api
      .get("/admin/clients")
      .then(({ data }) => {
        setClients(data);
        const preset = presetClientId && data.find((c) => c.id === presetClientId);
        if (preset) setSelected(preset);
        else if (data.length) setSelected(data[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetClientId]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/admin/messages/${selected.id}`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [selected]);

  const sendText = async () => {
    if (!text.trim() || !selected) return;
    try {
      const { data } = await api.post(`/admin/messages/${selected.id}`, { type: "text", content: text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    }
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", blob, "voice-note.webm");
      try {
        const { data: upload } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        const { data } = await api.post(`/admin/messages/${selected.id}`, { type: "audio", file_id: upload.file_id });
        setMessages((m) => [...m, data]);
      } catch {
        toast.error("Failed to send audio clip");
      }
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div data-testid="admin-messages-panel" className="grid lg:grid-cols-[280px_1fr] gap-4 h-[75vh]">
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl overflow-y-auto">
        {clients.map((c) => (
          <button
            key={c.id}
            data-testid={`message-client-${c.id}`}
            onClick={() => setSelected(c)}
            className={`w-full text-left px-4 py-3 border-b border-[#27272A] last:border-0 transition-colors ${selected?.id === c.id ? "bg-white/5" : ""}`}
          >
            <p className="text-sm text-white">{c.name}</p>
            <p className="text-xs text-[#71717A]">{c.email}</p>
          </button>
        ))}
        {!clients.length && <p className="p-4 text-sm text-[#71717A]">No clients yet.</p>}
      </div>
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl flex flex-col">
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={`message-bubble-${m.id}`}
              className={`max-w-md px-4 py-2.5 rounded-2xl text-sm ${
                m.sender === "admin" ? "bg-[#D4AF37] text-black ml-auto" : "bg-[#0A0A0A] text-white border border-[#27272A]"
              }`}
            >
              {m.type === "text" ? m.content : <audio controls src={fileUrl(m.file_id)} className="w-56" />}
            </div>
          ))}
          {!messages.length && <p className="text-sm text-[#71717A]">Select a client to view messages.</p>}
        </div>
        {selected && (
          <div className="border-t border-[#27272A] p-4 flex items-center gap-2">
            <input
              data-testid="message-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendText()}
              placeholder="Type a message..."
              className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-full px-4 py-2.5 text-white text-sm"
            />
            <button
              data-testid="message-record-audio-button"
              onClick={recording ? stopRecording : startRecording}
              className={`p-2.5 rounded-full transition-colors ${recording ? "bg-red-500 text-white" : "bg-white/10 text-white"}`}
            >
              {recording ? <Stop size={18} /> : <Microphone size={18} />}
            </button>
            <button data-testid="message-send-button" onClick={sendText} className="p-2.5 rounded-full bg-[#D4AF37] text-black">
              <PaperPlaneTilt size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
